import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployee } from "../api/employee";
import { addTradeFormEvent, isTradeEventEligible } from "../api/trade";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import GiveawayRequestForm from "../components/GiveawayRequestForm";
import PageLoading from "../components/PageLoading";
import ShiftTradeForm from "../components/ShiftTradeForm";
import TimeTradeForm from "../components/TimeTradeForm";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { isDSTDay } from "../util/dateHelper";
import { getAdminStation, getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
  STATUS_APPROVED,
} from "../util/constants.js";
import { getUserDataFromEmployeeService } from "../util/employeeDataRetriever";
import {
  validateAdminComments,
  validateDuplicateShifts,
  validateEmployee,
  validateEmployeeId,
  validateEmployeeOneDataForAdminCreateTrade,
  validateEmployeeTwoDataForAdminCreateTrade,
  validateOwnTradeEmployeeId,
  validateReliefShift,
  validateShiftEndDateForAdminCreateTrade,
  validateShiftEndTime,
  validateShiftStartDateForAdminCreateTrade,
  validateShiftStartTimeForAdminCreateTrade,
  validateShiftWorkgroup,
  validateStation,
  validateTradeFormType,
} from "../util/inputValidation.js";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_SUBMIT_THE_TRADE_MESSAGE,
  REFERENCE_NUMBER_SUB_TITLE,
  TRADE_CONFIRMATION_TITLE,
  TRADE_CREATED_MESSAGE,
  TRADE_ELIGIBILITY_ERROR_MESSAGE,
} from "../util/messageConstants";
import { formatEmployeeIdFromKronos } from "../util/miscFormatter";
import { buildCreateTradeFormEventRequestBody } from "../util/tradeFormEventBuilder.js";
import {
  getIsTradeBetweenAgentAndSup,
  getUnionContractGivenWorkgroup,
  getWorkgroupGivenJobCode,
} from "../util/workgroupMapper.js";
import { isUserAuthorizedByServer } from "./../api/userAccess";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice.js";
import { getTradeWorkgroups } from "../util/tradeUtils.js";

const AdminCreateTradePage = (props) => {
  const isAdminPage = true;
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupData = useSelector(selectWorkgroupData);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isTradeFormSubmissionTriggered, setIsTradeFormSubmissionTriggered] = useState(false);
  const [isTradeFormSubmissionLoading, setIsTradeFormSubmissionLoading] = useState(false);
  const [isTradeFormSubmissionError, setIsTradeFormSubmissionError] = useState(false);
  const [tradeFormSubmissionErrors, setTradeFormSubmissionErrors] = useState("");
  const [isTradeSuccessDialogOpen, setTradeSuccessDialog] = useState(false);
  const [isTradeEventEligibilityWarningDialogOpen, setIsTradeEventEligibilityWarningDialogOpen] = useState(false);
  const tradeEligibilityErrorMessage = useRef("");
  const dispatch = useDispatch();
  // Generic Trade Form State
  const [selectedStation, setSelectedStation] = useState("");
  const [adminStations, setAdminStations] = useState([]);
  const [stationTouched, setStationTouched] = useState(false);
  const [stationErrors, setStationErrors] = useState("");
  const [tradeFormType, setTradeFormType] = useState("");
  const [tradeFormTypeErrors, setTradeFormTypeErrors] = useState("");
  const [eligibleWorkgroups, setEligibleWorkgroups] = useState([]);
  const [workgroup, setWorkgroup] = useState("");
  const [unionContract, setUnionContract] = useState("");
  const [workgroupErrors, setWorkgroupErrors] = useState("");
  const [createdTrade, setCreatedTrade] = useState(null);
  // Specific Trade Form Type State
  const [offeredEmployeeId, setOfferedEmployeeId] = useState("");
  const [offeredEmployeeIdSearchTriggered, setOfferedEmployeeIdSearchTriggered] = useState(false);
  const [offeredEmployeeIdSearchLoaded, setOfferedEmployeeIdSearchLoaded] = useState(false);
  const [offeredEmployeeIdErrors, setOfferedEmployeeIdErrors] = useState("");
  const [offeredEmployeeData, setOfferedEmployeeData] = useState(null);
  const [offeredEmployeeFirstName, setOfferedEmployeeFirstName] = useState("");
  const [offeredEmployeeLastName, setOfferedEmployeeLastName] = useState("");
  const [offeredEmployeeWorkgroup, setOfferedEmployeeWorkgroup] = useState("");
  const [offeredEmployeeTempStation, setOfferedEmployeeTempStation] = useState("");
  const [offeredEmployeeHomeStation, setOfferedEmployeeHomeStation] = useState("");

  const [offeredShiftStart, setOfferedShiftStart] = useState(null);
  const [offeredShiftStartErrors, setOfferedShiftStartErrors] = useState("");
  const [offeredShiftStartHour, setOfferedShiftStartHour] = useState("");
  const [offeredShiftStartMinute, setOfferedShiftStartMinute] = useState("");
  const [offeredShiftStartTimeErrors, setOfferedShiftStartTimeErrors] = useState("");
  const [offeredShiftStartHourTouched, setOfferedShiftStartHourTouched] = useState(false);
  const [offeredShiftStartMinuteTouched, setOfferedShiftStartMinuteTouched] = useState(false);
  const [offeredShiftEnd, setOfferedShiftEnd] = useState(null);
  const [offeredShiftEndErrors, setOfferedShiftEndErrors] = useState("");
  const [offeredShiftEndTouched, setOfferedShiftEndTouched] = useState(false);
  const [offeredShiftEndHour, setOfferedShiftEndHour] = useState("");
  const [offeredShiftEndMinute, setOfferedShiftEndMinute] = useState("");
  const [offeredShiftEndTimeErrors, setOfferedShiftEndTimeErrors] = useState("");
  const [offeredShiftEndHourTouched, setOfferedShiftEndHourTouched] = useState(false);
  const [offeredShiftEndMinuteTouched, setOfferedShiftEndMinuteTouched] = useState(false);
  const [isOfferedReliefShift, setIsOfferedReliefShift] = useState("");
  const [offeredReliefShiftErrors, setOfferedReliefShiftErrors] = useState("");
  const [requestedEmployeeId, setRequestedEmployeeId] = useState("");
  const [requestedEmployeeIdSearchTriggered, setRequestedEmployeeIdSearchTriggered] = useState(false);
  const [requestedEmployeeIdSearchLoaded, setRequestedEmployeeIdSearchLoaded] = useState(false);
  const [requestedEmployeeIdErrors, setRequestedEmployeeIdErrors] = useState("");
  const [requestedEmployeeData, setRequestedEmployeeData] = useState(null);
  const [requestedEmployeeFirstName, setRequestedEmployeeFirstName] = useState("");
  const [requestedEmployeeLastName, setRequestedEmployeeLastName] = useState("");
  const [requestedEmployeeWorkgroup, setRequestedEmployeeWorkgroup] = useState("");
  const [requestedEmployeeTempStation, setRequestedEmployeeTempStation] = useState("");
  const [requestedEmployeeHomeStation, setRequestedEmployeeHomeStation] = useState("");

  const [requestedShiftStart, setRequestedShiftStart] = useState(null);
  const [requestedShiftStartErrors, setRequestedShiftStartErrors] = useState("");
  const [requestedShiftStartHour, setRequestedShiftStartHour] = useState("");
  const [requestedShiftStartMinute, setRequestedShiftStartMinute] = useState("");
  const [requestedShiftStartTimeErrors, setRequestedShiftStartTimeErrors] = useState("");
  const [requestedShiftStartHourTouched, setRequestedShiftStartHourTouched] = useState(false);
  const [requestedShiftStartMinuteTouched, setRequestedShiftStartMinuteTouched] = useState(false);
  const [requestedShiftEnd, setRequestedShiftEnd] = useState(null);
  const [requestedShiftEndErrors, setRequestedShiftEndErrors] = useState("");
  const [requestedShiftEndTouched, setRequestedShiftEndTouched] = useState(false);
  const [requestedShiftEndHour, setRequestedShiftEndHour] = useState("");
  const [requestedShiftEndMinute, setRequestedShiftEndMinute] = useState("");
  const [requestedShiftEndTimeErrors, setRequestedShiftEndTimeErrors] = useState("");
  const [requestedShiftEndHourTouched, setRequestedShiftEndHourTouched] = useState(false);
  const [requestedShiftEndMinuteTouched, setRequestedShiftEndMinuteTouched] = useState(false);
  const [isRequestedReliefShift, setIsRequestedReliefShift] = useState("");
  const [requestedReliefShiftErrors, setRequestedReliefShiftErrors] = useState("");

  const [comments, setComments] = useState("");
  const [commentsTouched, setCommentsTouched] = useState(false);
  const [commentsErrors, setCommentsErrors] = useState("");
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [isOfferedShiftDSTWarningOpen, setIsOfferedShiftDSTWarningOpen] = useState(false);
  const [isRequestedShiftDSTWarningOpen, setIsRequestedShiftDSTWarningOpen] = useState(false);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      setIsPageLoading(false);
      setSelectedStation(getAdminStation(props.featureConfig.userStations, userSession, stationFilter));
      setAdminStations(getMultiStationAdminStationList(userSession));
      setEligibleWorkgroups(getTradeWorkgroups(Object.keys(workgroupData)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  useEffect(() => {
    if (offeredShiftStart && offeredShiftEnd) {
      if (isDSTDay(offeredShiftStart) || isDSTDay(offeredShiftEnd)) {
        setIsOfferedShiftDSTWarningOpen(true);
      } else {
        setIsOfferedShiftDSTWarningOpen(false);
      }
    }
  }, [offeredShiftStart, offeredShiftEnd]);

  useEffect(() => {
    if (requestedShiftStart && requestedShiftEnd) {
      if (isDSTDay(requestedShiftStart) || isDSTDay(requestedShiftEnd)) {
        setIsRequestedShiftDSTWarningOpen(true);
      } else {
        setIsRequestedShiftDSTWarningOpen(false);
      }
    }
  }, [requestedShiftStart, requestedShiftEnd]);

  const clearBottomForm = () => {
    setOfferedEmployeeId("");
    setOfferedEmployeeIdSearchTriggered(false);
    setOfferedEmployeeIdSearchLoaded(false);
    setOfferedEmployeeIdErrors("");
    setOfferedEmployeeData(null);
    setOfferedEmployeeFirstName("");
    setOfferedEmployeeLastName("");
    setOfferedEmployeeWorkgroup("");
    setOfferedEmployeeTempStation("");
    setOfferedEmployeeHomeStation("");
    setOfferedShiftStart(null);
    setOfferedShiftStartErrors("");
    setOfferedShiftStartHour("");
    setOfferedShiftStartMinute("");
    setOfferedShiftStartTimeErrors("");
    setOfferedShiftStartHourTouched(false);
    setOfferedShiftStartMinuteTouched(false);
    setOfferedShiftEnd(null);
    setOfferedShiftEndErrors("");
    setOfferedShiftEndTouched(false);
    setOfferedShiftEndHour("");
    setOfferedShiftEndMinute("");
    setOfferedShiftEndTimeErrors("");
    setOfferedShiftEndHourTouched(false);
    setOfferedShiftEndMinuteTouched(false);
    setIsOfferedReliefShift(false);
    setOfferedReliefShiftErrors("");
    setRequestedEmployeeId("");
    setRequestedEmployeeIdSearchTriggered(false);
    setRequestedEmployeeIdSearchLoaded(false);
    setRequestedEmployeeIdErrors("");
    setRequestedEmployeeData(null);
    setRequestedEmployeeFirstName("");
    setRequestedEmployeeLastName("");
    setRequestedEmployeeWorkgroup("");
    setRequestedEmployeeTempStation("");
    setRequestedEmployeeHomeStation("");
    setRequestedShiftStart(null);
    setRequestedShiftStartErrors("");
    setRequestedShiftStartHour("");
    setRequestedShiftStartMinute("");
    setRequestedShiftStartTimeErrors("");
    setRequestedShiftStartHourTouched(false);
    setRequestedShiftStartMinuteTouched(false);
    setRequestedShiftEnd(null);
    setRequestedShiftEndErrors("");
    setRequestedShiftEndTouched(false);
    setRequestedShiftEndHour("");
    setRequestedShiftEndMinute("");
    setRequestedShiftEndTimeErrors("");
    setRequestedShiftEndHourTouched(false);
    setRequestedShiftEndMinuteTouched(false);
    setIsRequestedReliefShift(false);
    setRequestedReliefShiftErrors("");
    setComments("");
    setCommentsTouched(false);
    setCommentsErrors("");
    setIsOfferedShiftDSTWarningOpen(false);
    setIsRequestedShiftDSTWarningOpen(false);
  };

  const setDuplicateShiftErrors = (error) => {
    setOfferedShiftStartErrors(error);
    setOfferedShiftStartTimeErrors(error === "" ? "" : " ");
    setOfferedShiftEndErrors(error);
    setOfferedShiftEndTimeErrors(error === "" ? "" : " ");
    setRequestedShiftStartErrors(error);
    setRequestedShiftStartTimeErrors(error === "" ? "" : " ");
    setRequestedShiftEndErrors(error);
    setRequestedShiftEndTimeErrors(error === "" ? "" : " ");
  };

  const handleStationChange = (event) => {
    clearBottomForm();
    setSelectedStation(event.target.value);
    dispatch(updateStationFilter(event.target.value));
  };

  const handleStationBlur = (event) => {
    setStationErrors(validateStation(event.target.value));
    setStationTouched(true);
  };

  const handleTradeFormTypeSelectChange = (event) => {
    clearBottomForm();
    setTradeFormType(event.target.value);
    setTradeFormTypeErrors(validateTradeFormType(event.target.value));
  };

  const handleTradeFormTypeSelectBlur = (event) => {
    setTradeFormTypeErrors(validateTradeFormType(event.target.value));
  };

  const handleWorkgroupSelectChange = (event) => {
    clearBottomForm();
    setWorkgroup(event.target.value);
    const workgroupErrors = validateShiftWorkgroup(event.target.value);
    setWorkgroupErrors(workgroupErrors);
    if (workgroupErrors === "") {
      const selectedWorkgroupUnionContract = getUnionContractGivenWorkgroup(event.target.value);
      setUnionContract(selectedWorkgroupUnionContract);
    }
  };

  const handleWorkgroupSelectBlur = (event) => {
    setWorkgroupErrors(validateShiftWorkgroup(event.target.value));
  };

  const prepareNewTradeEventPayload = () => {
    let newTradeEventPayload = {
      isAdminCreated: true,
      isPageLoading: isPageLoading,
      station: selectedStation,
      workgroup: workgroup,
      isSupervisorInvolved: getIsTradeBetweenAgentAndSup(offeredEmployeeWorkgroup, requestedEmployeeWorkgroup),
      tradeFormType: tradeFormType,
      offeredEmployeeId: offeredEmployeeId,
      offeredEmployeeFirstName: offeredEmployeeFirstName,
      offeredEmployeeLastName: offeredEmployeeLastName,
      offeredEmployeeWorkgroup: offeredEmployeeWorkgroup,
      offeredShiftStart: offeredShiftStart,
      offeredShiftStartHour: offeredShiftStartHour,
      offeredShiftStartMinute: offeredShiftStartMinute,
      offeredShiftEnd: offeredShiftEnd,
      offeredShiftEndHour: offeredShiftEndHour,
      offeredShiftEndMinute: offeredShiftEndMinute,
      isOfferedReliefShift: isOfferedReliefShift,
      requestedEmployeeId: requestedEmployeeId,
      requestedEmployeeFirstName: requestedEmployeeFirstName,
      requestedEmployeeLastName: requestedEmployeeLastName,
      requestedEmployeeWorkgroup: requestedEmployeeWorkgroup,
      requestedShiftStart: requestedShiftStart,
      requestedShiftStartHour: requestedShiftStartHour,
      requestedShiftStartMinute: requestedShiftStartMinute,
      requestedShiftEnd: requestedShiftEnd,
      requestedShiftEndHour: requestedShiftEndHour,
      requestedShiftEndMinute: requestedShiftEndMinute,
      isRequestedReliefShift: isRequestedReliefShift,
      reasonComments: comments,
    };
    return newTradeEventPayload;
  };

  const handleSubmitClicked = async () => {
    if (tradeFormType === "Giveaway" && !isGiveawayFormValidationSuccess()) {
      setIsSubmitDialogOpen(false);
      return;
    }
    if (tradeFormType !== "Giveaway" && !isFormValidationSuccess()) {
      setIsSubmitDialogOpen(false);
      return;
    }
    let newTradeEventPayload = prepareNewTradeEventPayload();
    setIsTradeFormSubmissionError(false);
    setIsTradeFormSubmissionTriggered(true);
    setIsTradeFormSubmissionLoading(true);

    const isTradeEligible = await isTradeEventEligible(newTradeEventPayload);

    if (!isTradeEligible) {
      tradeEligibilityErrorMessage.current = TRADE_ELIGIBILITY_ERROR_MESSAGE;
      setIsTradeFormSubmissionTriggered(false);
      setIsTradeFormSubmissionLoading(false);
      setIsSubmitDialogOpen(false);
      setIsTradeEventEligibilityWarningDialogOpen(true);
      return;
    } else {
      tradeEligibilityErrorMessage.current = "";
    }

    let payload = JSON.stringify(
      buildCreateTradeFormEventRequestBody(STATUS_APPROVED, newTradeEventPayload, userSession)
    );

    addTradeFormEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.error) {
          console.error("Error");
          setTradeFormSubmissionErrors(result.error);
          setIsTradeFormSubmissionError(true);
          setIsTradeFormSubmissionLoading(false);
          setIsSubmitDialogOpen(false);
        } else {
          console.log("Success!");
          setCreatedTrade(result.newTrades);
          setIsSubmitDialogOpen(false);
          setTradeSuccessDialog(true);
          setIsTradeFormSubmissionLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSubmitDialogOpen(false);
        setIsTradeFormSubmissionLoading(false);
      });
  };

  const fetchOfferedEmployeeData = (eid) => {
    setOfferedEmployeeIdSearchLoaded(false);
    setOfferedEmployeeIdSearchTriggered(true);
    setOfferedEmployeeIdErrors("");
    if (isLinkedEmployeeErrors(null)) {
      setRequestedEmployeeIdErrors("");
    }
    setOfferedEmployeeData(null);
    setOfferedEmployeeFirstName("");
    setOfferedEmployeeLastName("");
    setOfferedEmployeeWorkgroup("");
    setOfferedEmployeeTempStation("");
    setOfferedEmployeeHomeStation("");
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = await getUserDataFromEmployeeService(result);
          let employeeDataErrors = validateEmployeeOneDataForAdminCreateTrade(
            employeeData,
            selectedStation,
            requestedEmployeeData,
            workgroup
          );
          if (employeeDataErrors === "") {
            // data fields are populated, now do deeper validation
            employeeDataErrors = validateEmployee(
              employeeData.eid,
              employeeData.firstName,
              employeeData.lastName,
              employeeData.activeStation,
              getWorkgroupGivenJobCode(employeeData.jobCode)
            );
          }
          if (employeeDataErrors !== "") {
            if (isLinkedEmployeeErrors(employeeDataErrors)) {
              setRequestedEmployeeIdErrors(employeeDataErrors);
            }
            setOfferedEmployeeIdErrors(employeeDataErrors);
            setOfferedEmployeeIdSearchLoaded(true);
          }
          setOfferedEmployeeData(employeeData);
          setOfferedEmployeeFirstName(employeeData.firstName ? employeeData.firstName : "");
          setOfferedEmployeeLastName(employeeData.lastName ? employeeData.lastName : "");
          setOfferedEmployeeWorkgroup(
            getWorkgroupGivenJobCode(employeeData.jobCode) ? getWorkgroupGivenJobCode(employeeData.jobCode) : ""
          );
          setOfferedEmployeeTempStation(employeeData.tempStation ? employeeData.tempStation : "");
          setOfferedEmployeeHomeStation(employeeData.homeStation ? employeeData.homeStation : "");
          setOfferedEmployeeIdSearchLoaded(true);
        } else {
          setOfferedEmployeeIdErrors("Invalid Employee ID");
          setOfferedEmployeeIdSearchLoaded(true);
        }
      });
  };

  const fetchRequestedEmployeeData = (eid) => {
    setRequestedEmployeeIdSearchLoaded(false);
    setRequestedEmployeeIdSearchTriggered(true);
    setRequestedEmployeeIdErrors("");
    if (isLinkedEmployeeErrors(null)) {
      setOfferedEmployeeIdErrors("");
    }
    setRequestedEmployeeData(null);
    setRequestedEmployeeFirstName("");
    setRequestedEmployeeLastName("");
    setRequestedEmployeeWorkgroup("");
    setRequestedEmployeeTempStation("");
    setRequestedEmployeeHomeStation("");
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = await getUserDataFromEmployeeService(result);
          let employeeDataErrors = validateEmployeeTwoDataForAdminCreateTrade(
            employeeData,
            selectedStation,
            offeredEmployeeData,
            workgroup,
            tradeFormType
          );
          if (employeeDataErrors === "") {
            // data fields are populated, now do deeper validation
            employeeDataErrors = validateEmployee(
              employeeData.eid,
              employeeData.firstName,
              employeeData.lastName,
              employeeData.activeStation,
              getWorkgroupGivenJobCode(employeeData.jobCode)
            );
          }
          if (employeeDataErrors !== "") {
            if (isLinkedEmployeeErrors(employeeDataErrors)) {
              setOfferedEmployeeIdErrors(employeeDataErrors);
            }
            setRequestedEmployeeIdErrors(employeeDataErrors);
            setRequestedEmployeeIdSearchLoaded(true);
          }
          setRequestedEmployeeData(employeeData);
          setRequestedEmployeeFirstName(employeeData.firstName ? employeeData.firstName : "");
          setRequestedEmployeeLastName(employeeData.lastName ? employeeData.lastName : "");
          setRequestedEmployeeWorkgroup(
            getWorkgroupGivenJobCode(employeeData.jobCode) ? getWorkgroupGivenJobCode(employeeData.jobCode) : ""
          );
          setRequestedEmployeeTempStation(employeeData.tempStation ? employeeData.tempStation : "");
          setRequestedEmployeeHomeStation(employeeData.homeStation ? employeeData.homeStation : "");
          setRequestedEmployeeIdSearchLoaded(true);
        } else {
          setRequestedEmployeeIdErrors("Invalid Employee ID");
          setRequestedEmployeeIdSearchLoaded(true);
        }
      });
  };

  const isLinkedEmployeeErrors = (incomingError) => {
    if (incomingError) {
      if (
        incomingError.includes("At least 1 employee must work in") ||
        incomingError.includes("Employees ineligible to trade") ||
        incomingError.includes("Employee #2 is the same as Employee #1. Please select another Employee.")
      ) {
        return true;
      }
    }
    if (
      requestedEmployeeIdErrors.includes("At least 1 employee must work in") ||
      requestedEmployeeIdErrors.includes("Employees ineligible to trade") ||
      requestedEmployeeIdErrors.includes("Employee #2 is the same as Employee #1. Please select another Employee.") ||
      offeredEmployeeIdErrors.includes("At least 1 employee must work in")
    ) {
      return true;
    }
    return false;
  };

  //   Specific Trade Form Type State Handlers - START
  const handleOfferedEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleOfferedEmployeeIdBlur(event);
    }
  };

  const handleOfferedEmployeeIdChange = (event) => {
    if (offeredEmployeeData) {
      setOfferedEmployeeData(null);
      setOfferedEmployeeFirstName("");
      setOfferedEmployeeLastName("");
      setOfferedEmployeeTempStation("");
      setOfferedEmployeeHomeStation("");
      setOfferedEmployeeWorkgroup("");
    }
    setOfferedEmployeeId(event.target.value.toLowerCase());
  };

  const handleOfferedEmployeeIdBlur = (event) => {
    let trimEmpId = event.target.value.trim();
    let errors = validateEmployeeId(trimEmpId) + validateOwnTradeEmployeeId(trimEmpId, userSession.eid);
    if (offeredEmployeeIdErrors === "" || errors !== "") {
      setOfferedEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!offeredEmployeeData || trimEmpId !== formatEmployeeIdFromKronos(offeredEmployeeData.kronosPersonNumber)) {
        fetchOfferedEmployeeData(trimEmpId);
      }
    }
  };

  const handleOfferedShiftStartChange = (date) => {
    setOfferedShiftStart(date);
    setOfferedShiftEnd(date);
    let offeredShiftStartErrors = validateShiftStartDateForAdminCreateTrade(
      tradeFormType,
      date,
      requestedShiftStart,
      selectedStation,
      false
    );
    let offeredShiftStartTimeErrors = "";
    let offeredShiftEndErrors = "";
    let offeredShiftEndTimeErrors = "";
    setOfferedShiftStartErrors(offeredShiftStartErrors);

    if (offeredShiftStartHourTouched && offeredShiftStartMinuteTouched) {
      offeredShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute
      );
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
    }

    if (offeredShiftEndTouched) {
      offeredShiftEndErrors = validateShiftEndDateForAdminCreateTrade(date, date, selectedStation);
      setOfferedShiftEndErrors(offeredShiftEndErrors);
    }

    if (offeredShiftEndHourTouched && offeredShiftEndMinuteTouched) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        date,
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        selectedStation
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (
      offeredShiftStartErrors === "" &&
      offeredShiftStartTimeErrors === "" &&
      offeredShiftEndErrors === "" &&
      offeredShiftEndTimeErrors === ""
    ) {
      let duplicateShiftErrors = validateDuplicateShifts(
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        date,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftStartHourChange = (event) => {
    setOfferedShiftStartHour(event.target.value);
    setOfferedShiftStartHourTouched(true);
    let offeredShiftStartTimeErrors = "";
    let offeredShiftEndTimeErrors = "";

    if (offeredShiftStartMinuteTouched) {
      offeredShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
        offeredShiftStart,
        event.target.value,
        offeredShiftStartMinute
      );
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
    }

    if (offeredShiftStartMinuteTouched && offeredShiftEndHourTouched && offeredShiftEndMinuteTouched) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        event.target.value,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        selectedStation
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftStartTimeErrors === "" && offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        event.target.value,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftStartMinuteChange = (event) => {
    setOfferedShiftStartMinute(event.target.value);
    setOfferedShiftStartMinuteTouched(true);
    let offeredShiftStartTimeErrors = "";
    let offeredShiftEndTimeErrors = "";

    if (offeredShiftStartHourTouched) {
      offeredShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
        offeredShiftStart,
        offeredShiftStartHour,
        event.target.value
      );
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
    }

    if (offeredShiftStartHourTouched && offeredShiftEndHourTouched && offeredShiftEndMinuteTouched) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        offeredShiftStartHour,
        event.target.value,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        selectedStation
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftStartTimeErrors === "" && offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        event.target.value,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftEndChange = (date) => {
    setOfferedShiftEnd(date);
    setOfferedShiftEndTouched(true);
    let offeredShiftEndErrors = validateShiftEndDateForAdminCreateTrade(offeredShiftStart, date, selectedStation);
    let offeredShiftEndTimeErrors = "";
    setOfferedShiftEndErrors(offeredShiftEndErrors);

    if (offeredShiftEndHourTouched && offeredShiftEndMinuteTouched) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        selectedStation
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftEndErrors === "" && offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        date,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftEndHourChange = (event) => {
    setOfferedShiftEndHour(event.target.value);
    setOfferedShiftEndHourTouched(true);
    let offeredShiftEndTimeErrors = "";

    if (offeredShiftEndMinuteTouched && offeredShiftStartHourTouched && offeredShiftStartMinuteTouched) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        event.target.value,
        offeredShiftEndMinute,
        unionContract,
        selectedStation
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        event.target.value,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftEndMinuteChange = (event) => {
    setOfferedShiftEndMinute(event.target.value);
    setOfferedShiftEndMinuteTouched(true);
    let offeredShiftEndTimeErrors = "";

    if (offeredShiftEndHourTouched && offeredShiftStartHourTouched && offeredShiftStartMinuteTouched) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        event.target.value,
        unionContract,
        selectedStation
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        event.target.value,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedReliefShiftSelectChange = (event) => {
    setIsOfferedReliefShift(event.target.value);
    setOfferedReliefShiftErrors(validateReliefShift(event.target.value));
  };

  const handleRequestedEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleRequestedEmployeeIdBlur(event);
    }
  };

  const handleRequestedEmployeeIdChange = (event) => {
    if (requestedEmployeeData) {
      setRequestedEmployeeData(null);
      setRequestedEmployeeFirstName("");
      setRequestedEmployeeLastName("");
      setRequestedEmployeeTempStation("");
      setRequestedEmployeeHomeStation("");
      setRequestedEmployeeWorkgroup("");
    }
    setRequestedEmployeeId(event.target.value.toLowerCase());
  };

  const handleRequestedEmployeeIdBlur = (event) => {
    let trimEmpId = event.target.value.trim();
    let errors = validateEmployeeId(trimEmpId) + validateOwnTradeEmployeeId(trimEmpId, userSession.eid);

    if (requestedEmployeeIdErrors === "" || errors !== "") {
      setRequestedEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (
        !requestedEmployeeData ||
        trimEmpId !== formatEmployeeIdFromKronos(requestedEmployeeData.kronosPersonNumber)
      ) {
        fetchRequestedEmployeeData(trimEmpId);
      }
    }
  };

  const handleRequestedShiftStartChange = (date) => {
    setRequestedShiftStart(date);
    setRequestedShiftEnd(date);
    let requestedShiftStartErrors = validateShiftStartDateForAdminCreateTrade(
      tradeFormType,
      date,
      offeredShiftStart,
      selectedStation,
      false
    );
    let requestedShiftStartTimeErrors = "";
    let requestedShiftEndErrors = "";
    let requestedShiftEndTimeErrors = "";
    setRequestedShiftStartErrors(requestedShiftStartErrors);

    if (requestedShiftStartHourTouched && requestedShiftStartMinuteTouched) {
      requestedShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute
      );
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
    }

    if (requestedShiftEndTouched) {
      requestedShiftEndErrors = validateShiftEndDateForAdminCreateTrade(date, date, selectedStation);
      setRequestedShiftEndErrors(requestedShiftEndErrors);
    }

    if (requestedShiftEndHourTouched && requestedShiftEndMinuteTouched) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        date,
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        selectedStation
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (
      requestedShiftStartErrors === "" &&
      requestedShiftStartTimeErrors === "" &&
      requestedShiftEndErrors === "" &&
      requestedShiftEndTimeErrors === ""
    ) {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        date,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftStartHourChange = (event) => {
    setRequestedShiftStartHour(event.target.value);
    setRequestedShiftStartHourTouched(true);
    let requestedShiftStartTimeErrors = "";
    let requestedShiftEndTimeErrors = "";

    if (requestedShiftStartMinuteTouched) {
      requestedShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
        requestedShiftStart,
        event.target.value,
        requestedShiftStartMinute
      );
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
    }

    if (requestedShiftStartMinuteTouched && requestedShiftEndHourTouched && requestedShiftEndMinuteTouched) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        event.target.value,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        selectedStation
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftStartTimeErrors === "" && requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        event.target.value,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftStartMinuteChange = (event) => {
    setRequestedShiftStartMinute(event.target.value);
    setRequestedShiftStartMinuteTouched(true);
    let requestedShiftStartTimeErrors = "";
    let requestedShiftEndTimeErrors = "";

    if (requestedShiftStartHourTouched) {
      requestedShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
        requestedShiftStart,
        requestedShiftStartHour,
        event.target.value
      );
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
    }

    if (requestedShiftStartHourTouched && requestedShiftEndHourTouched && requestedShiftEndMinuteTouched) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        requestedShiftStartHour,
        event.target.value,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        selectedStation
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftStartTimeErrors === "" && requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        event.target.value,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftEndChange = (date) => {
    setRequestedShiftEnd(date);
    setRequestedShiftEndTouched(true);
    let requestedShiftEndErrors = validateShiftEndDateForAdminCreateTrade(requestedShiftStart, date, selectedStation);
    let requestedShiftEndTimeErrors = "";
    setRequestedShiftEndErrors(requestedShiftEndErrors);

    if (requestedShiftEndHourTouched && requestedShiftEndMinuteTouched) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        selectedStation
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftEndErrors === "" && requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        date,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftEndHourChange = (event) => {
    setRequestedShiftEndHour(event.target.value);
    setRequestedShiftEndHourTouched(true);
    let requestedShiftEndTimeErrors = "";

    if (requestedShiftEndMinuteTouched && requestedShiftStartHourTouched && requestedShiftStartMinuteTouched) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        event.target.value,
        requestedShiftEndMinute,
        unionContract,
        selectedStation
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        event.target.value,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftEndMinuteChange = (event) => {
    setRequestedShiftEndMinute(event.target.value);
    setRequestedShiftEndMinuteTouched(true);
    let requestedShiftEndTimeErrors = "";

    if (requestedShiftEndHourTouched && requestedShiftStartHourTouched && requestedShiftStartMinuteTouched) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        event.target.value,
        unionContract,
        selectedStation
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        event.target.value,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedReliefShiftSelectChange = (event) => {
    setIsRequestedReliefShift(event.target.value);
    setRequestedReliefShiftErrors(validateReliefShift(event.target.value));
  };

  const handleCommentsChange = (event) => {
    setComments(event.target.value);
    setCommentsTouched(true);
  };

  const handleCommentsBlur = (event) => {
    setCommentsTouched(true);
    setCommentsErrors(validateAdminComments(event.target.value));
  };

  const handleCommentsClear = () => {
    setComments("");
    setCommentsErrors(validateAdminComments(""));
  };

  const handleClickSubmitDialogOpen = () => {
    if (tradeFormType === "Giveaway" && !isGiveawayFormValidationSuccess()) {
      return;
    }
    if (tradeFormType !== "Giveaway" && !isFormValidationSuccess()) {
      return;
    }
    setIsSubmitDialogOpen(true);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    setIsCancelDialogOpen(true);
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsCancelDialogOpen(false);
  };

  const handleEmpOneDataClear = () => {
    setOfferedEmployeeId("");
    setOfferedEmployeeData(null);
    setOfferedEmployeeFirstName("");
    setOfferedEmployeeLastName("");
    setOfferedEmployeeTempStation("");
    setOfferedEmployeeHomeStation("");
    setOfferedEmployeeWorkgroup("");
    let errors = validateEmployeeId("");
    if (errors !== "") {
      setOfferedEmployeeIdErrors(errors);
    }
  };

  const handleEmpTwoDataClear = () => {
    setRequestedEmployeeId("");
    setRequestedEmployeeData(null);
    setRequestedEmployeeFirstName("");
    setRequestedEmployeeLastName("");
    setRequestedEmployeeTempStation("");
    setRequestedEmployeeHomeStation("");
    setRequestedEmployeeWorkgroup("");
    let errors = validateEmployeeId("");
    if (errors !== "") {
      setRequestedEmployeeIdErrors(errors);
    }
  };

  const isFormValidationSuccess = () => {
    const stationErrors = validateStation(selectedStation);
    const tradeFormTypeErrors = validateTradeFormType(tradeFormType);
    const shiftWorkgroupErrors = validateShiftWorkgroup(workgroup);

    let employeeOneDataErrors = validateEmployeeOneDataForAdminCreateTrade(
      offeredEmployeeData,
      selectedStation,
      requestedEmployeeData,
      workgroup
    );
    if (employeeOneDataErrors === "") {
      // data fields are populated, now do deeper validation
      employeeOneDataErrors = validateEmployee(
        offeredEmployeeData.eid,
        offeredEmployeeData.firstName,
        offeredEmployeeData.lastName,
        offeredEmployeeData.activeStation,
        getWorkgroupGivenJobCode(offeredEmployeeData.jobCode)
      );
    }

    let employeeTwoDataErrors = validateEmployeeTwoDataForAdminCreateTrade(
      requestedEmployeeData,
      selectedStation,
      offeredEmployeeData,
      workgroup,
      tradeFormType
    );
    if (employeeTwoDataErrors === "") {
      // data fields are populated, now do deeper validation
      employeeTwoDataErrors = validateEmployee(
        requestedEmployeeData.eid,
        requestedEmployeeData.firstName,
        requestedEmployeeData.lastName,
        requestedEmployeeData.activeStation,
        getWorkgroupGivenJobCode(requestedEmployeeData.jobCode)
      );
    }

    const duplicateShiftErrors = validateDuplicateShifts(
      offeredShiftStart,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      offeredShiftEnd,
      offeredShiftEndHour,
      offeredShiftEndMinute,
      requestedShiftStart,
      requestedShiftStartHour,
      requestedShiftStartMinute,
      requestedShiftEnd,
      requestedShiftEndHour,
      requestedShiftEndMinute,
      isAdminPage
    );
    const offeredShiftStartErrors = validateShiftStartDateForAdminCreateTrade(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      selectedStation,
      true
    );
    const offeredShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
      offeredShiftStart,
      offeredShiftStartHour,
      offeredShiftStartMinute
    );
    const offeredShiftEndErrors = validateShiftEndDateForAdminCreateTrade(
      offeredShiftStart,
      offeredShiftEnd,
      selectedStation
    );
    const offeredShiftEndTimeErrors = validateShiftEndTime(
      offeredShiftStart,
      offeredShiftEnd,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      offeredShiftEndHour,
      offeredShiftEndMinute,
      unionContract,
      selectedStation
    );
    const offeredReliefShiftErrors = validateReliefShift(isOfferedReliefShift);
    const requestedShiftStartErrors = validateShiftStartDateForAdminCreateTrade(
      tradeFormType,
      requestedShiftStart,
      offeredShiftStart,
      selectedStation,
      true
    );
    const requestedShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
      requestedShiftStart,
      requestedShiftStartHour,
      requestedShiftStartMinute
    );
    const requestedShiftEndErrors = validateShiftEndDateForAdminCreateTrade(
      requestedShiftStart,
      requestedShiftEnd,
      selectedStation
    );
    const requestedShiftEndTimeErrors = validateShiftEndTime(
      requestedShiftStart,
      requestedShiftEnd,
      requestedShiftStartHour,
      requestedShiftStartMinute,
      requestedShiftEndHour,
      requestedShiftEndMinute,
      unionContract,
      selectedStation
    );
    const requestedReliefShiftErrors = validateReliefShift(isRequestedReliefShift);
    const adminCommentsErrors = validateAdminComments(comments);

    setStationTouched(true);
    setStationErrors(stationErrors);
    setTradeFormTypeErrors(tradeFormTypeErrors);
    setWorkgroupErrors(shiftWorkgroupErrors);

    if (isTopFormFilledOut()) {
      if (employeeOneDataErrors !== "") {
        if (isLinkedEmployeeErrors(employeeOneDataErrors)) {
          setRequestedEmployeeIdErrors(employeeOneDataErrors);
        }
        setOfferedEmployeeIdErrors(employeeOneDataErrors);
        setOfferedEmployeeIdSearchLoaded(true);
      }

      if (employeeTwoDataErrors !== "") {
        if (isLinkedEmployeeErrors(employeeTwoDataErrors)) {
          setOfferedEmployeeIdErrors(employeeTwoDataErrors);
        }
        setRequestedEmployeeIdErrors(employeeTwoDataErrors);
        setRequestedEmployeeIdSearchLoaded(true);
      }

      setOfferedShiftStartErrors(offeredShiftStartErrors);
      setOfferedShiftStartHourTouched(true);
      setOfferedShiftStartMinuteTouched(true);
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
      setOfferedShiftEndErrors(offeredShiftEndErrors);
      setOfferedShiftEndTouched(true);
      setOfferedShiftEndHourTouched(true);
      setOfferedShiftEndMinuteTouched(true);
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
      setOfferedReliefShiftErrors(offeredReliefShiftErrors);
      setRequestedShiftStartErrors(requestedShiftStartErrors);
      setRequestedShiftStartHourTouched(true);
      setRequestedShiftStartMinuteTouched(true);
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
      setRequestedShiftEndErrors(requestedShiftEndErrors);
      setRequestedShiftEndTouched(true);
      setRequestedShiftEndHourTouched(true);
      setRequestedShiftEndMinuteTouched(true);
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
      setRequestedReliefShiftErrors(requestedReliefShiftErrors);
      setCommentsTouched(true);
      setCommentsErrors(adminCommentsErrors);
      if (
        offeredShiftStartErrors === "" &&
        offeredShiftStartTimeErrors === "" &&
        offeredShiftEndErrors === "" &&
        offeredShiftEndTimeErrors === "" &&
        requestedShiftStartErrors === "" &&
        requestedShiftStartTimeErrors === "" &&
        requestedShiftEndErrors === "" &&
        requestedShiftEndTimeErrors === ""
      ) {
        setDuplicateShiftErrors(duplicateShiftErrors);
      }
    }

    return (
      stationErrors === "" &&
      tradeFormTypeErrors === "" &&
      shiftWorkgroupErrors === "" &&
      employeeOneDataErrors === "" &&
      employeeTwoDataErrors === "" &&
      offeredEmployeeIdErrors === "" &&
      duplicateShiftErrors === "" &&
      offeredShiftStartErrors === "" &&
      offeredShiftStartTimeErrors === "" &&
      offeredShiftEndErrors === "" &&
      offeredShiftEndTimeErrors === "" &&
      offeredReliefShiftErrors === "" &&
      requestedEmployeeIdErrors === "" &&
      requestedShiftStartErrors === "" &&
      requestedShiftStartTimeErrors === "" &&
      requestedShiftEndErrors === "" &&
      requestedShiftEndTimeErrors === "" &&
      requestedReliefShiftErrors === "" &&
      adminCommentsErrors === ""
    );
  };

  const isGiveawayFormValidationSuccess = () => {
    const stationErrors = validateStation(selectedStation);
    const tradeFormTypeErrors = validateTradeFormType(tradeFormType);
    const shiftWorkgroupErrors = validateShiftWorkgroup(workgroup);

    let employeeOneDataErrors = validateEmployeeOneDataForAdminCreateTrade(
      offeredEmployeeData,
      selectedStation,
      requestedEmployeeData,
      workgroup
    );
    if (employeeOneDataErrors === "") {
      // data fields are populated, now do deeper validation
      employeeOneDataErrors = validateEmployee(
        offeredEmployeeData.eid,
        offeredEmployeeData.firstName,
        offeredEmployeeData.lastName,
        offeredEmployeeData.activeStation,
        getWorkgroupGivenJobCode(offeredEmployeeData.jobCode)
      );
    }

    let employeeTwoDataErrors = validateEmployeeTwoDataForAdminCreateTrade(
      requestedEmployeeData,
      selectedStation,
      offeredEmployeeData,
      workgroup,
      tradeFormType
    );
    if (employeeTwoDataErrors === "") {
      // data fields are populated, now do deeper validation
      employeeTwoDataErrors = validateEmployee(
        requestedEmployeeData.eid,
        requestedEmployeeData.firstName,
        requestedEmployeeData.lastName,
        requestedEmployeeData.activeStation,
        getWorkgroupGivenJobCode(requestedEmployeeData.jobCode)
      );
    }

    const offeredShiftStartErrors = validateShiftStartDateForAdminCreateTrade(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      selectedStation,
      true
    );
    const offeredShiftStartTimeErrors = validateShiftStartTimeForAdminCreateTrade(
      offeredShiftStart,
      offeredShiftStartHour,
      offeredShiftStartMinute
    );
    const offeredShiftEndErrors = validateShiftEndDateForAdminCreateTrade(
      offeredShiftStart,
      offeredShiftEnd,
      selectedStation
    );
    const offeredShiftEndTimeErrors = validateShiftEndTime(
      offeredShiftStart,
      offeredShiftEnd,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      offeredShiftEndHour,
      offeredShiftEndMinute,
      unionContract,
      selectedStation
    );
    const offeredReliefShiftErrors = validateReliefShift(isOfferedReliefShift);
    const adminCommentsErrors = validateAdminComments(comments);

    setStationTouched(true);
    setStationErrors(stationErrors);
    setTradeFormTypeErrors(tradeFormTypeErrors);
    setWorkgroupErrors(shiftWorkgroupErrors);

    if (isTopFormFilledOut()) {
      if (employeeOneDataErrors !== "") {
        if (isLinkedEmployeeErrors(employeeOneDataErrors)) {
          setRequestedEmployeeIdErrors(employeeOneDataErrors);
        }
        setOfferedEmployeeIdErrors(employeeOneDataErrors);
        setOfferedEmployeeIdSearchLoaded(true);
      }

      if (employeeTwoDataErrors !== "") {
        if (isLinkedEmployeeErrors(employeeTwoDataErrors)) {
          setOfferedEmployeeIdErrors(employeeTwoDataErrors);
        }
        setRequestedEmployeeIdErrors(employeeTwoDataErrors);
        setRequestedEmployeeIdSearchLoaded(true);
      }

      setOfferedShiftStartErrors(offeredShiftStartErrors);
      setOfferedShiftStartHourTouched(true);
      setOfferedShiftStartMinuteTouched(true);
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
      setOfferedShiftEndErrors(offeredShiftEndErrors);
      setOfferedShiftEndTouched(true);
      setOfferedShiftEndHourTouched(true);
      setOfferedShiftEndMinuteTouched(true);
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
      setOfferedReliefShiftErrors(offeredReliefShiftErrors);
      setRequestedEmployeeIdErrors(requestedEmployeeIdErrors);
      setCommentsTouched(true);
      setCommentsErrors(adminCommentsErrors);
    }

    return (
      stationErrors === "" &&
      tradeFormTypeErrors === "" &&
      shiftWorkgroupErrors === "" &&
      employeeOneDataErrors === "" &&
      employeeTwoDataErrors === "" &&
      offeredEmployeeIdErrors === "" &&
      offeredShiftStartErrors === "" &&
      offeredShiftStartTimeErrors === "" &&
      offeredShiftEndErrors === "" &&
      offeredShiftEndTimeErrors === "" &&
      offeredReliefShiftErrors === "" &&
      requestedEmployeeIdErrors === "" &&
      adminCommentsErrors === ""
    );
  };

  const getReferenceNumberForCreatedTrade = () => {
    return createdTrade[0].referenceNumber;
  };

  const isTopFormFilledOut = () => {
    return selectedStation !== "" && tradeFormType !== "" && workgroup !== "";
  };
  //   ShiftTradeForm State Handlers - END

  const renderTradeForm = (tradeFormTypeName) => {
    switch (tradeFormTypeName) {
      case "Shift Trade":
        return (
          <ShiftTradeForm
            isAdminCreateTrade={true}
            station={selectedStation}
            jobCode={userSession.jobCode} // TODO: We shouldn't need this later, backend needs to be able to take workgroup instead
            offeredEmployeeId={offeredEmployeeId}
            offeredEmployeeIdErrors={offeredEmployeeIdErrors}
            offeredEmployeeSearchTriggered={offeredEmployeeIdSearchTriggered}
            offeredEmployeeSearchLoaded={offeredEmployeeIdSearchLoaded}
            offeredEmployeeFirstName={offeredEmployeeFirstName}
            offeredEmployeeLastName={offeredEmployeeLastName}
            offeredEmployeeWorkgroup={offeredEmployeeWorkgroup}
            offeredEmployeeTempStation={offeredEmployeeTempStation}
            offeredEmployeeHomeStation={offeredEmployeeHomeStation}
            offeredShiftStart={offeredShiftStart}
            offeredShiftStartErrors={offeredShiftStartErrors}
            offeredShiftStartHour={offeredShiftStartHour}
            offeredShiftStartMinute={offeredShiftStartMinute}
            offeredShiftStartTimeErrors={offeredShiftStartTimeErrors}
            offeredShiftEnd={offeredShiftEnd}
            offeredShiftEndErrors={offeredShiftEndErrors}
            offeredShiftEndHour={offeredShiftEndHour}
            offeredShiftEndMinute={offeredShiftEndMinute}
            offeredShiftEndTimeErrors={offeredShiftEndTimeErrors}
            isOfferedReliefShift={isOfferedReliefShift}
            offeredReliefShiftErrors={offeredReliefShiftErrors}
            requestedEmployeeId={requestedEmployeeId}
            requestedEmployeeIdErrors={requestedEmployeeIdErrors}
            requestedEmployeeSearchTriggered={requestedEmployeeIdSearchTriggered}
            requestedEmployeeSearchLoaded={requestedEmployeeIdSearchLoaded}
            requestedEmployeeFirstName={requestedEmployeeFirstName}
            requestedEmployeeLastName={requestedEmployeeLastName}
            requestedEmployeeWorkgroup={requestedEmployeeWorkgroup}
            requestedEmployeeTempStation={requestedEmployeeTempStation}
            requestedEmployeeHomeStation={requestedEmployeeHomeStation}
            requestedShiftStart={requestedShiftStart}
            requestedShiftStartErrors={requestedShiftStartErrors}
            requestedShiftStartHour={requestedShiftStartHour}
            requestedShiftStartMinute={requestedShiftStartMinute}
            requestedShiftStartTimeErrors={requestedShiftStartTimeErrors}
            requestedShiftEnd={requestedShiftEnd}
            requestedShiftEndErrors={requestedShiftEndErrors}
            requestedShiftEndHour={requestedShiftEndHour}
            requestedShiftEndMinute={requestedShiftEndMinute}
            requestedShiftEndTimeErrors={requestedShiftEndTimeErrors}
            isRequestedReliefShift={isRequestedReliefShift}
            requestedReliefShiftErrors={requestedReliefShiftErrors}
            comments={comments}
            commentsTouched={commentsTouched}
            commentsErrors={commentsErrors}
            onEmpOneClear={handleEmpOneDataClear}
            onEmpOneChange={handleOfferedEmployeeIdChange}
            onEmpOneBlur={handleOfferedEmployeeIdBlur}
            onEmpOneEnterKeyPressed={handleOfferedEmployeeIdEnterKeyPressed}
            onOfferedShiftStartChange={handleOfferedShiftStartChange}
            onOfferedShiftStartHourChange={handleOfferedShiftStartHourChange}
            onOfferedShiftStartMinuteChange={handleOfferedShiftStartMinuteChange}
            onOfferedShiftEndChange={handleOfferedShiftEndChange}
            onOfferedShiftEndHourChange={handleOfferedShiftEndHourChange}
            onOfferedShiftEndMinuteChange={handleOfferedShiftEndMinuteChange}
            onOfferedReliefShiftSelectChange={handleOfferedReliefShiftSelectChange}
            onEmpTwoClear={handleEmpTwoDataClear}
            onEmpTwoChange={handleRequestedEmployeeIdChange}
            onEmpTwoBlur={handleRequestedEmployeeIdBlur}
            onEmpTwoEnterKeyPressed={handleRequestedEmployeeIdEnterKeyPressed}
            onRequestedShiftStartChange={handleRequestedShiftStartChange}
            onRequestedShiftStartHourChange={handleRequestedShiftStartHourChange}
            onRequestedShiftStartMinuteChange={handleRequestedShiftStartMinuteChange}
            onRequestedShiftEndChange={handleRequestedShiftEndChange}
            onRequestedShiftEndHourChange={handleRequestedShiftEndHourChange}
            onRequestedShiftEndMinuteChange={handleRequestedShiftEndMinuteChange}
            onRequestedReliefShiftSelectChange={handleRequestedReliefShiftSelectChange}
            onCommentsClear={handleCommentsClear}
            onCommentsChange={handleCommentsChange}
            onCommentsBlur={handleCommentsBlur}
            isOfferedShiftDSTWarningOpen={isOfferedShiftDSTWarningOpen}
            isRequestedShiftDSTWarningOpen={isRequestedShiftDSTWarningOpen}
          />
        );
      case "Time Trade":
        return (
          <TimeTradeForm
            isAdminCreateTrade={true}
            station={selectedStation}
            jobCode={userSession.jobCode} // TODO: We shouldn't need this later, backend needs to be able to take workgroup instead
            workgroup={workgroup}
            unionContract={unionContract}
            offeredEmployeeId={offeredEmployeeId}
            offeredEmployeeIdErrors={offeredEmployeeIdErrors}
            offeredEmployeeSearchTriggered={offeredEmployeeIdSearchTriggered}
            offeredEmployeeSearchLoaded={offeredEmployeeIdSearchLoaded}
            offeredEmployeeFirstName={offeredEmployeeFirstName}
            offeredEmployeeLastName={offeredEmployeeLastName}
            offeredEmployeeWorkgroup={offeredEmployeeWorkgroup}
            offeredEmployeeTempStation={offeredEmployeeTempStation}
            offeredEmployeeHomeStation={offeredEmployeeHomeStation}
            offeredShiftStart={offeredShiftStart}
            offeredShiftStartErrors={offeredShiftStartErrors}
            offeredShiftStartHour={offeredShiftStartHour}
            offeredShiftStartMinute={offeredShiftStartMinute}
            offeredShiftStartTimeErrors={offeredShiftStartTimeErrors}
            offeredShiftEnd={offeredShiftEnd}
            offeredShiftEndErrors={offeredShiftEndErrors}
            offeredShiftEndHour={offeredShiftEndHour}
            offeredShiftEndMinute={offeredShiftEndMinute}
            offeredShiftEndTimeErrors={offeredShiftEndTimeErrors}
            isOfferedReliefShift={isOfferedReliefShift}
            offeredReliefShiftErrors={offeredReliefShiftErrors}
            requestedEmployeeId={requestedEmployeeId}
            requestedEmployeeIdErrors={requestedEmployeeIdErrors}
            requestedEmployeeSearchTriggered={requestedEmployeeIdSearchTriggered}
            requestedEmployeeSearchLoaded={requestedEmployeeIdSearchLoaded}
            requestedEmployeeFirstName={requestedEmployeeFirstName}
            requestedEmployeeLastName={requestedEmployeeLastName}
            requestedEmployeeWorkgroup={requestedEmployeeWorkgroup}
            requestedEmployeeTempStation={requestedEmployeeTempStation}
            requestedEmployeeHomeStation={requestedEmployeeHomeStation}
            requestedShiftStart={requestedShiftStart}
            requestedShiftStartErrors={requestedShiftStartErrors}
            requestedShiftStartHour={requestedShiftStartHour}
            requestedShiftStartMinute={requestedShiftStartMinute}
            requestedShiftStartTimeErrors={requestedShiftStartTimeErrors}
            requestedShiftEnd={requestedShiftEnd}
            requestedShiftEndErrors={requestedShiftEndErrors}
            requestedShiftEndHour={requestedShiftEndHour}
            requestedShiftEndMinute={requestedShiftEndMinute}
            requestedShiftEndTimeErrors={requestedShiftEndTimeErrors}
            isRequestedReliefShift={isRequestedReliefShift}
            requestedReliefShiftErrors={requestedReliefShiftErrors}
            comments={comments}
            commentsTouched={commentsTouched}
            commentsErrors={commentsErrors}
            onEmpOneClear={handleEmpOneDataClear}
            onEmpOneChange={handleOfferedEmployeeIdChange}
            onEmpOneBlur={handleOfferedEmployeeIdBlur}
            onEmpOneEnterKeyPressed={handleOfferedEmployeeIdEnterKeyPressed}
            onOfferedShiftStartChange={handleOfferedShiftStartChange}
            onOfferedShiftStartHourChange={handleOfferedShiftStartHourChange}
            onOfferedShiftStartMinuteChange={handleOfferedShiftStartMinuteChange}
            onOfferedShiftEndChange={handleOfferedShiftEndChange}
            onOfferedShiftEndHourChange={handleOfferedShiftEndHourChange}
            onOfferedShiftEndMinuteChange={handleOfferedShiftEndMinuteChange}
            onOfferedReliefShiftSelectChange={handleOfferedReliefShiftSelectChange}
            onEmpTwoClear={handleEmpTwoDataClear}
            onEmpTwoChange={handleRequestedEmployeeIdChange}
            onEmpTwoBlur={handleRequestedEmployeeIdBlur}
            onEmpTwoEnterKeyPressed={handleRequestedEmployeeIdEnterKeyPressed}
            onRequestedShiftStartChange={handleRequestedShiftStartChange}
            onRequestedShiftStartHourChange={handleRequestedShiftStartHourChange}
            onRequestedShiftStartMinuteChange={handleRequestedShiftStartMinuteChange}
            onRequestedShiftEndChange={handleRequestedShiftEndChange}
            onRequestedShiftEndHourChange={handleRequestedShiftEndHourChange}
            onRequestedShiftEndMinuteChange={handleRequestedShiftEndMinuteChange}
            onRequestedReliefShiftSelectChange={handleRequestedReliefShiftSelectChange}
            onCommentsClear={handleCommentsClear}
            onCommentsChange={handleCommentsChange}
            onCommentsBlur={handleCommentsBlur}
            isOfferedShiftDSTWarningOpen={isOfferedShiftDSTWarningOpen}
            isRequestedShiftDSTWarningOpen={isRequestedShiftDSTWarningOpen}
          />
        );
      case "Giveaway":
        return (
          <GiveawayRequestForm
            isAdminCreateTrade={true}
            station={selectedStation}
            jobCode={userSession.jobCode} // TODO: We shouldn't need this later, backend needs to be able to take workgroup instead
            offeredEmployeeId={offeredEmployeeId}
            offeredEmployeeIdErrors={offeredEmployeeIdErrors}
            offeredEmployeeSearchTriggered={offeredEmployeeIdSearchTriggered}
            offeredEmployeeSearchLoaded={offeredEmployeeIdSearchLoaded}
            offeredEmployeeFirstName={offeredEmployeeFirstName}
            offeredEmployeeLastName={offeredEmployeeLastName}
            offeredEmployeeWorkgroup={offeredEmployeeWorkgroup}
            offeredEmployeeTempStation={offeredEmployeeTempStation}
            offeredEmployeeHomeStation={offeredEmployeeHomeStation}
            offeredShiftStart={offeredShiftStart}
            offeredShiftStartErrors={offeredShiftStartErrors}
            offeredShiftStartHour={offeredShiftStartHour}
            offeredShiftStartMinute={offeredShiftStartMinute}
            offeredShiftStartTimeErrors={offeredShiftStartTimeErrors}
            offeredShiftEnd={offeredShiftEnd}
            offeredShiftEndErrors={offeredShiftEndErrors}
            offeredShiftEndHour={offeredShiftEndHour}
            offeredShiftEndMinute={offeredShiftEndMinute}
            offeredShiftEndTimeErrors={offeredShiftEndTimeErrors}
            isOfferedReliefShift={isOfferedReliefShift}
            offeredReliefShiftErrors={offeredReliefShiftErrors}
            requestedEmployeeId={requestedEmployeeId}
            requestedEmployeeIdErrors={requestedEmployeeIdErrors}
            requestedEmployeeSearchTriggered={requestedEmployeeIdSearchTriggered}
            requestedEmployeeSearchLoaded={requestedEmployeeIdSearchLoaded}
            requestedEmployeeFirstName={requestedEmployeeFirstName}
            requestedEmployeeLastName={requestedEmployeeLastName}
            requestedEmployeeWorkgroup={requestedEmployeeWorkgroup}
            requestedEmployeeTempStation={requestedEmployeeTempStation}
            requestedEmployeeHomeStation={requestedEmployeeHomeStation}
            comments={comments}
            commentsTouched={commentsTouched}
            commentsErrors={commentsErrors}
            onEmpOneClear={handleEmpOneDataClear}
            onEmpOneChange={handleOfferedEmployeeIdChange}
            onEmpOneBlur={handleOfferedEmployeeIdBlur}
            onEmpOneEnterKeyPressed={handleOfferedEmployeeIdEnterKeyPressed}
            onOfferedShiftStartChange={handleOfferedShiftStartChange}
            onOfferedShiftStartHourChange={handleOfferedShiftStartHourChange}
            onOfferedShiftStartMinuteChange={handleOfferedShiftStartMinuteChange}
            onOfferedShiftEndChange={handleOfferedShiftEndChange}
            onOfferedShiftEndHourChange={handleOfferedShiftEndHourChange}
            onOfferedShiftEndMinuteChange={handleOfferedShiftEndMinuteChange}
            onOfferedReliefShiftSelectChange={handleOfferedReliefShiftSelectChange}
            onEmpTwoClear={handleEmpTwoDataClear}
            onEmpTwoChange={handleRequestedEmployeeIdChange}
            onEmpTwoBlur={handleRequestedEmployeeIdBlur}
            onEmpTwoEnterKeyPressed={handleRequestedEmployeeIdEnterKeyPressed}
            onCommentsClear={handleCommentsClear}
            onCommentsChange={handleCommentsChange}
            onCommentsBlur={handleCommentsBlur}
            isOfferedShiftDSTWarningOpen={isOfferedShiftDSTWarningOpen}
          />
        );
      default:
        // TODO: What do you want this to be?
        console.log("Default?");
        return "";
    }
  };

  const renderStationMenuItem = (stationName) => {
    return (
      <MenuItem
        key={stationName}
        value={stationName}
        disabled={!props.featureConfig.userStations.includes(stationName)}
      >
        {stationName}
      </MenuItem>
    );
  };

  const renderTradeFormTypeMenuItem = () => {
    const tradeTypes = ["Giveaway", "Shift Trade", "Time Trade"];
    return tradeTypes
      .slice()
      .sort()
      .map((tradeType) => {
        return (
          <MenuItem key={tradeType} value={tradeType}>
            {tradeType}
          </MenuItem>
        );
      });
  };

  const renderEligibleWorkgroupMenuItems = (eligibleWorkgroups) => {
    if (eligibleWorkgroups) {
      return eligibleWorkgroups
        .slice()
        .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
        .map((workgroup) => {
          return (
            <MenuItem key={workgroup} value={workgroup}>
              {workgroup}
            </MenuItem>
          );
        });
    }
  };

  const renderSubmitAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        isDialogOpen={isSubmitDialogOpen}
        message={ARE_YOU_SURE_YOU_WANT_TO_SUBMIT_THE_TRADE_MESSAGE}
        handleNoButtonClicked={handleClickSubmitDialogClose}
        handleYesButtonClicked={handleSubmitClicked}
        isYesLoading={isTradeFormSubmissionTriggered && isTradeFormSubmissionLoading}
      />
    );
  };

  const renderCancelAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        isDialogOpen={isCancelDialogOpen}
        message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA)}
        handleNoButtonClicked={handleClickCancelDialogClose}
        redirectTo={props.featureConfig.adminPath}
      />
    );
  };
  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.TRADE_ADMINISTRATION,
    BREADCRUMBS_TITLE.CREATE_TRADE,
  ];
  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.adminPath];
  const confirmationRequired = () => {
    if (workgroup !== "" || tradeFormType !== "") {
      return true;
    } else {
      return false;
    }
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  const renderDesktopAdminCreateTradePage = () => {
    return (
      <>
        {isPageLoading ? (
          <CircularProgress />
        ) : (
          <>
            <StarsBreadcrumbs
              breadcrumbsList={breadcrumbsList}
              breadcrumbsLinks={breadcrumbsLinks}
              isConfirmationRequired={confirmationRequired()}
              cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
            />
            <div className="trade-form-page-main">
              <Grid item xs={12}>
                <Typography component={"div"}>
                  <Box className="page-title-left">Create Trade</Box>
                </Typography>
              </Grid>

              <Divider className="divider" />

              <Grid container>
                <Grid item xs={12}>
                  <Box mt={1} mb={1}>
                    {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                      stationTouched && stationErrors !== "" ? (
                        <FormControl variant="outlined">
                          <InputLabel>Station</InputLabel>
                          <Select
                            id="stationSelectElement"
                            className="select-field"
                            value={selectedStation}
                            onChange={handleStationChange}
                            onBlur={handleStationBlur}
                            label="Station"
                            error
                          >
                            <MenuItem value="" disabled>
                              <em>Trade Type</em>
                            </MenuItem>
                            {adminStations
                              .slice()
                              .sort()
                              .map((station) => {
                                return renderStationMenuItem(station);
                              })}
                          </Select>
                          <FormHelperText error>{tradeFormTypeErrors}</FormHelperText>
                        </FormControl>
                      ) : (
                        <FormControl variant="outlined">
                          <InputLabel>Station</InputLabel>
                          <Select
                            id="stationSelectElement"
                            className="select-field"
                            value={selectedStation}
                            onChange={handleStationChange}
                            onBlur={handleStationBlur}
                            label="Station"
                          >
                            <MenuItem value="" disabled>
                              <em>Station</em>
                            </MenuItem>
                            {adminStations
                              .slice()
                              .sort()
                              .map((station) => {
                                return renderStationMenuItem(station);
                              })}
                          </Select>
                        </FormControl>
                      )
                    ) : stationTouched && stationErrors !== "" ? (
                      <TextField
                        error
                        className="text-field"
                        label="Station"
                        variant="outlined"
                        value={selectedStation}
                        onChange={handleStationChange}
                        onBlur={handleStationBlur}
                        helperText={stationErrors}
                        disabled
                      />
                    ) : (
                      <TextField
                        className="text-field"
                        label="Station"
                        variant="outlined"
                        value={selectedStation}
                        onChange={handleStationChange}
                        onBlur={handleStationBlur}
                        disabled
                      />
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box mt={1} mb={1}>
                    {workgroupErrors !== "" ? (
                      <FormControl variant="outlined">
                        <InputLabel id="workgroupSelectLabel">Shift Workgroup</InputLabel>
                        <Select
                          labelId="workgroupSelectLabel"
                          id="workgroupSelectElement"
                          className="select-field"
                          value={workgroup}
                          onChange={handleWorkgroupSelectChange}
                          onBlur={handleWorkgroupSelectBlur}
                          label="Shift Workgroup"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Workgroup</em>
                          </MenuItem>
                          {renderEligibleWorkgroupMenuItems(eligibleWorkgroups)}
                        </Select>
                        <FormHelperText error>{workgroupErrors}</FormHelperText>
                      </FormControl>
                    ) : (
                      <FormControl variant="outlined">
                        <InputLabel id="workgroupSelectLabel">Shift Workgroup</InputLabel>
                        <Select
                          labelId="workgroupSelectLabel"
                          id="workgroupSelectElement"
                          className="select-field"
                          value={workgroup}
                          onChange={handleWorkgroupSelectChange}
                          onBlur={handleWorkgroupSelectBlur}
                          label="Shift Workgroup"
                        >
                          <MenuItem value="" disabled>
                            <em>Workgroup</em>
                          </MenuItem>
                          {renderEligibleWorkgroupMenuItems(eligibleWorkgroups)}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <Box mt={1} mb={1}>
                    {tradeFormTypeErrors !== "" ? (
                      <FormControl variant="outlined">
                        <InputLabel>Trade Type</InputLabel>
                        <Select
                          id="tradeFormTypeSelectElement"
                          className="select-field"
                          value={tradeFormType}
                          onChange={handleTradeFormTypeSelectChange}
                          onBlur={handleTradeFormTypeSelectBlur}
                          label="Trade Type"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Trade Type</em>
                          </MenuItem>
                          {renderTradeFormTypeMenuItem()}
                        </Select>
                        <FormHelperText error>{tradeFormTypeErrors}</FormHelperText>
                      </FormControl>
                    ) : (
                      <FormControl variant="outlined">
                        <InputLabel>Trade Type</InputLabel>
                        <Select
                          id="tradeFormTypeSelectElement"
                          className="select-field"
                          value={tradeFormType}
                          onChange={handleTradeFormTypeSelectChange}
                          onBlur={handleTradeFormTypeSelectBlur}
                          label="Trade Type"
                        >
                          <MenuItem value="" disabled>
                            <em>Trade Type</em>
                          </MenuItem>
                          {renderTradeFormTypeMenuItem()}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {isTopFormFilledOut() ? renderTradeForm(tradeFormType) : ""}

              {isTradeFormSubmissionError ? (
                <Alert severity="error" className="text-field">
                  <AlertTitle>{tradeFormSubmissionErrors}</AlertTitle>
                </Alert>
              ) : (
                ""
              )}

              <Box m={1}>
                <Button
                  className="button"
                  variant="outlined"
                  color="primary"
                  size="large"
                  fullWidth={true}
                  onClick={handleClickCancelDialogOpen}
                >
                  <Typography variant="h5" component="h2">
                    <Box className="order-selection-text">Cancel</Box>
                  </Typography>
                </Button>
              </Box>

              <Box m={1}>
                <div className="link" style={{ width: "100%" }}>
                  {isTradeFormSubmissionTriggered && isTradeFormSubmissionLoading ? (
                    <Button
                      className="button"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth={true}
                      onClick={handleClickSubmitDialogOpen}
                      disabled
                    >
                      <CircularProgress size={24} />
                    </Button>
                  ) : (
                    <Button
                      className="button"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth={true}
                      onClick={handleClickSubmitDialogOpen}
                    >
                      <Typography variant="h5" component="h2">
                        <Box className="order-selection-text">Submit</Box>
                      </Typography>
                    </Button>
                  )}
                </div>
              </Box>
            </div>
          </>
        )}
        {renderSubmitAreYouSureDialog()}
        {renderCancelAreYouSureDialog()}
      </>
    );
  };

  const renderMobileAdminCreateTradePage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  return (
    <div className="App-contents enable-scroll" data-testid="admin-create-trade-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>
          {props.isDesktopView ? renderDesktopAdminCreateTradePage() : renderMobileAdminCreateTradePage()}
          {isTradeSuccessDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isTradeSuccessDialogOpen}
              title={TRADE_CONFIRMATION_TITLE}
              subTitle={REFERENCE_NUMBER_SUB_TITLE(getReferenceNumberForCreatedTrade())}
              message={TRADE_CREATED_MESSAGE}
              redirectTo={props.featureConfig.adminPath}
            />
          )}
          {isTradeEventEligibilityWarningDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isTradeEventEligibilityWarningDialogOpen}
              title="Trade Eligibility"
              hasError={true}
              error={tradeEligibilityErrorMessage.current}
              handleOKClick={() => setIsTradeEventEligibilityWarningDialogOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminCreateTradePage;
